import React, { useEffect, useState } from 'react';
import {
  Box,
  CssBaseline,
  ThemeProvider,
  createTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
} from '@mui/material';
import Sidebar from './FlowEditor/Sidebar.tsx';
import ChatCanvas from './FlowEditor/ChatCanvas.tsx';
import ChatbotPopup from './ChatBox/ChatbotPopup.tsx';
import FlowCanvas from './FlowEditor/FlowCanvas.tsx';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  botFlowDataAtom,
  dynamicWebFormPreviewDataAtom,
  flowObjAtom,
  flowValidationAtom,
} from '../../Services/_states/index';
import { useNavigate, useParams } from 'react-router-dom';
import { FlowBuilderActions, LeadFormActions } from '../../Services/_actions/index.js';
import { isValidPhoneNumber } from '../../utils/helper.jsx';
import { useSnackBar } from './Common/SnackBar/index.tsx';
import { Edge, Node, TransformedNode } from '../../interfaces/Flowbuilder.interface.ts';
import { modifyEntitySettingObj } from './helper.tsx';
import { staticColumnsMatch } from '../Leads/DynamicFormFields/helper.ts';

const theme = createTheme({
  palette: { mode: 'light' },
});

const ChatbotBuilder: React.FC = () => {
  const actions = FlowBuilderActions();
  const leadFormActions = LeadFormActions();
  const { showSnackBar } = useSnackBar();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const flowObj = useRecoilValue(flowObjAtom);
  const [botFLowData, setBotFlowData] = useRecoilState < any > (botFlowDataAtom);
  const [webFormList, setWebFormList] = useRecoilState(dynamicWebFormPreviewDataAtom);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { flowId } = useParams();
  const flowValidationObj = useSetRecoilState(flowValidationAtom);

  useEffect(() => {
    flowValidationObj({});
  }, []);

  const isConnectionMissing = (nodeId: string, handle: string) => {
    return !flowObj?.edges.some((edge: any) => edge.sourceHandle === `${nodeId}-${handle}`);
  };

  const validateNodes = (nodes: Node[]): Record<string, any> => {
    const errors: Record<string, any> = {};

    nodes.forEach((node) => {
      const { type, data, id } = node;
      const nodeErrors: Record<string, any> = {};

      if (!['product', 'existingFlow', 'template', 'conditional'].includes(type) && !data?.body) {
        nodeErrors.body = 'Body is missing';
      }

      if (type === 'intervention') {
        const allDisabled = data?.intervention?.every((item: any) => item.enabled === false);
        if (allDisabled) {
          nodeErrors.errorMsg = 'At least one item is enabled';
        }
        const isClickToCallEnabled = data?.intervention?.some(
          (item: any) => item.type === 'click_to_call' && item.enabled === true
        );
        if ((!data?.agentList || data?.agentList?.length < 1) && isClickToCallEnabled) {
          nodeErrors.agentList = 'Please select at least one agent';
        } else {
          const valueError: { [key: number]: string } = {};
          const connectionError: { [key: number]: string } = {};
          data?.intervention?.forEach((item: any, index: any) => {
            if (item?.enabled) {
              if (!item.value || item?.value?.trim() === '')
                valueError[index] = `${item?.label} value is required`;
              if (isConnectionMissing(id, `${item.value}`))
                connectionError[index] = 'Connection is missing';
            }
          });
          if (Object.keys(valueError).length) nodeErrors.valueError = valueError;
          if (Object.keys(connectionError).length) nodeErrors.connectionError = connectionError;
        }
      }

      if (['image', 'video', 'audio', 'document'].includes(type) && !data?.media?.s3_object?.url) {
        nodeErrors.url = `${type.charAt(0).toUpperCase() + type.slice(1)} URL is missing`;
      }
      if (type === 'list' && data?.sourceType === 'Custom') {
        const groupErrors: Record<number, string> = {};
        const optionErrors: Record<number, Record<number, string>> = {};

        if (!data?.options?.length) {
          nodeErrors.options = 'At least one group is required.';
        } else {
          const seenTitles = new Set();
          data.options.forEach((group: any, groupIndex) => {
            const trimmedTitle = group?.title?.trim().toLowerCase() || '';
            if (!group?.title?.trim()) {
              groupErrors[groupIndex] = 'Group title is required.';
            } else if (seenTitles.has(trimmedTitle)) {
              groupErrors[groupIndex] = 'Group title must not be duplicate.';
            } else {
              seenTitles.add(trimmedTitle);
            }
            if (!group?.options?.length) {
              groupErrors[groupIndex] =
                groupErrors[groupIndex] || 'At least one option value is required.';
            } else {
              const groupOptionErrors: Record<number, string> = {};
              group.options.forEach((option, optionIndex) => {
                if (!option?.trim()) {
                  groupOptionErrors[optionIndex] = 'Option value is required.';
                } else if (isConnectionMissing(id, `options-${groupIndex}-${optionIndex}`)) {
                  groupOptionErrors[optionIndex] = 'Connection is missing';
                }
              });
              if (Object.keys(groupOptionErrors).length)
                optionErrors[groupIndex] = groupOptionErrors;
            }
          });
        }
        if (Object.keys(groupErrors).length) nodeErrors.groupErrors = groupErrors;
        if (Object.keys(optionErrors).length) nodeErrors.optionErrors = optionErrors;
      }

      if (type === 'button') {
        if (
          ['image', 'video', 'document'].includes(data?.headerType?.value) &&
          !data?.media?.s3_object?.url
        ) {
          nodeErrors.url = `${data.headerType.value.charAt(0).toUpperCase() + data.headerType.value.slice(1)} URL is missing`;
        }
        if (!data?.buttons?.length) {
          nodeErrors.errorMsg = 'At least one button is required.';
        } else if (data.buttons.some((btn: any) => !btn.trim())) {
          nodeErrors.errorMsg = 'All buttons must have non-empty values.';
        }

        const connectionError: Record<number, string> = {};
        data?.buttons?.forEach((_, index) => {
          if (isConnectionMissing(id, `button-${index}`))
            connectionError[index] = 'Connection is missing';
        });
        if (Object.keys(connectionError).length) nodeErrors.connectionError = connectionError;
      }

      if (type === 'whatsAppFlow') {
        ['flowId', 'flowToken', 'firstScreenName'].forEach((field) => {
          if (!data?.[field]) nodeErrors[field] = `${field.replace(/([A-Z])/g, ' $1')} is required`;
        });
      }
      if (type === 'existingFlow' && !data?.selectedFlow?._id) {
        nodeErrors.existingFlow = 'Existing Flow is required';
      }

      if (Object.keys(nodeErrors).length) {
        errors[id] = nodeErrors;
      }
    });
    return errors;
  };

  const fetchTargetBySourceHandle = (sourceHandle: string) => {
    const edge: any = flowObj?.edges?.find((edge) => edge?.sourceHandle === sourceHandle);
    return edge ? edge?.target : null;
  };

  const findExistingFlowNode = (nodeId: string) => {
    const existingFlow: any = flowObj?.nodes?.find((node: Node) => node?.id === nodeId);
    if (existingFlow?.type === 'existingFlow') return existingFlow;
    else return null;
  };
  const convertNodesAndEdges = (nodes: Node[], edges: Edge[]): { rules: TransformedNode[] } => {
    const rules: TransformedNode[] = nodes.map((node) => {
      const { id, type, data: nodeData } = node;

      const inputContext = edges.filter((edge) => edge.target === id).map((edge) => edge.source);
      // const outputContext = edges.filter((edge) => edge.source === id).map((edge) => edge.target);
      const outputContext = edges
        ?.filter((edge) => edge?.source === id)
        ?.map((edge) => ({
          intent_id: edge?.target,
          flow_id: flowId,
        }));
      // const entityParams = nodeData?.params?.entity ? { ...nodeData?.params?.entity } : {};
      // const filteredEntityParams = Object.fromEntries(
      //   Object.entries(entityParams)?.filter(([key]) =>
      //     key === "flowId" || nodeData?.body?.includes(key)
      //   )
      // );
      const updatedParams = { ...nodeData?.params };
      const transformedNode: TransformedNode = {
        intent_id: id,
        sub_pattern: nodeData?.triggerSettings?.sub_pattern || [],
        default_params: nodeData?.triggerSettings?.default_params || [],
        input_context: inputContext?.length ? inputContext : [],
        output_context: outputContext?.length ? outputContext : [],
        is_first: inputContext?.length === 0,
        ai_mode_enabled: nodeData?.ai_mode_enabled ?? false,
        ai_query_parameters: nodeData?.ai_query_parameters || [],
        message: {
          type: type.toUpperCase(),
          header: { type: 'TEXT', text: nodeData?.header || '' },
          body: { type: 'TEXT', text: nodeData?.body || '', parameters: updatedParams || {} },
          footer: { type: 'TEXT', text: nodeData?.footer || '' },
          ...(type === 'existingFlow' &&
            nodeData?.selectedFlow && {
            selectedFlow: {
              _id: nodeData?.selectedFlow._id,
              title: nodeData?.selectedFlow.title
            }
          }),
        },
      };

      if (type === 'ctaUrl') {
        transformedNode.message.type = 'CTAURL';
        // transformedNode.message.header = { type: 'TEXT', text: nodeData.header || '' };
        // transformedNode.message.body = { type: 'TEXT', text: nodeData.body || '', parameters: nodeData.params || {} };
        // transformedNode.message.footer = { type: 'TEXT', text: nodeData.footer || '' };
        transformedNode.message.buttons = [
          {
            label: nodeData.ctaLabel ?? '',
            value: nodeData.ctaUrl ?? '',
            type: 'url',
          },
        ];
      }

      if (type === 'openAi') {
        transformedNode.message.body = {
          ...transformedNode.message.body,
          system_message: nodeData?.body || '',
        };
        delete transformedNode.message.body.text;
      }
      // if (type !== 'button' && type !== 'list' && outputContext?.length > 0) {
      //   const existingFlow = findExistingFlowNode(outputContext[0]?.intent_id);
      //   if (existingFlow) {
      //     const flowData = existingFlow?.data?.selectedFlow;
      //     const flowFirstNode = flowData?.rules?.find((rule) => rule?.is_first);
      //     transformedNode.output_context = [
      //       { intent_id: flowFirstNode?.intent_id, flow_id: flowData?._id || '' },
      //     ];
      //   }
      // }
      transformedNode.trigger = {};
      if (inputContext?.length === 0)
        transformedNode.trigger = { type: 'PATTERNS', values: botFLowData?.patterns };
      // if (inputContext?.length === 0) {
      //   const subPatterns = nodeData?.triggerSettings?.sub_pattern || [];
      //   transformedNode.trigger = {
      //     type: 'PATTERNS',
      //     values: [...(botFLowData?.patterns || []), ...subPatterns],
      //   };
      // }

      transformedNode.result = nodeData?.triggerSettings?.result || {};
      if (nodeData?.triggerSettings?.isSchedulerEnabled) {
        transformedNode.schedulerSettings = {
          scheduleType: nodeData.triggerSettings.schedulerSettings.scheduleType,
          delay: nodeData.triggerSettings.schedulerSettings.delay,
          startDate: nodeData.triggerSettings.schedulerSettings.startDate,
          endDate: nodeData.triggerSettings.schedulerSettings.endDate,
          time: nodeData.triggerSettings.schedulerSettings.time,
          node_type: nodeData.triggerSettings.schedulerSettings.node_type,
          message: nodeData.triggerSettings.schedulerSettings.message,
        };
      }

      if (nodeData.triggerSettings?.isTriggerEnable) {
        transformedNode.trigger = { type: 'ESCALATION' };
        if (nodeData?.triggerSettings?.triggerOption?.value === 'manual') {
          transformedNode.actions = {
            send_to: nodeData?.triggerSettings?.phoneNumbers?.map((phoneNumber) => ({
              recipient_type: nodeData?.triggerSettings?.triggerOption?.value || '',
              message: {
                action: {
                  template_id: nodeData?.triggerSettings?.selectedTemplate?.wa_Id || '',
                  template_name: nodeData?.triggerSettings?.selectedTemplate?.name || '',
                  language: nodeData?.triggerSettings?.selectedTemplate?.language || '',
                },
              },
              phone_number: phoneNumber,
            })),
          };
        } else if (nodeData?.triggerSettings?.triggerOption?.value === 'userTable') {
          transformedNode.actions = {
            send_to: nodeData?.triggerSettings?.selectedUserAndRoles?.map((phoneNumber) => ({
              recipient_type: nodeData?.triggerSettings?.triggerOption?.value || '',
              message: {
                action: {
                  template_id: nodeData?.triggerSettings?.selectedTemplate?.wa_Id || '',
                },
              },
              phone_number: phoneNumber?.mobile || '',
            })),
          };
        }

        // send_to: [
        //   {
        //     recipient: 'USER',
        //     message: {
        //       header: { type: 'TEXT', text: 'We are escalating your query.' },
        //       body: {
        //         type: 'TEXT',
        //         text: 'Your request has been forwarded to our support team.',
        //       },
        //     },
        //   },
        //   {
        //     recipient: 'SUPPORT_TEAM',
        //     message: {
        //       header: { type: 'TEXT', text: 'New Escalation Alert' },
        //       body: { type: 'TEXT', text: 'A user has requested support.' },
        //     },
        //   },
        // ],
      }

      if (type === 'button' && nodeData?.buttons) {
        const mediaTypeMap = {
          image: 'image',
          video: 'video',
          document: 'document',
        };
        const headerTypeValue = nodeData?.headerType?.value;
        if (mediaTypeMap[headerTypeValue]) {
          transformedNode.message.header = {
            type: mediaTypeMap[headerTypeValue],
            media: nodeData?.media || {},
          };
        }
        transformedNode.trigger = { type: 'PATTERNS', values: nodeData.buttons };
        transformedNode.message.type = 'BUTTONS';
        transformedNode.message.buttons = nodeData?.buttons?.map((button: any, index) => {
          const uniqueContext = fetchTargetBySourceHandle(`${id}-button-${index}`);
          const existingFlow = findExistingFlowNode(`${uniqueContext}`);
          if (existingFlow) {
            const flowData = existingFlow?.data?.selectedFlow;
            const flowFirstNode = flowData?.rules?.find((rule) => rule?.is_first);
            return {
              label: button || '',
              value: button || '',
              // type: button || "text",
              // ...(button?.type === "url" && { url: button?.url || '' }),
              action: {
                output_context: uniqueContext || null,
                flow_id: flowId,
              },
            };
          } else {
            return {
              label: button || '',
              value: button || '',
              // type: button?.type || "text",
              // ...(button?.type === "url" && { url: button?.url || '' }),
              action: {
                output_context: uniqueContext || null,
                flow_id: flowId,
              },
            };
          }
        });
      } else if (type === 'whatsAppFlow') {
        transformedNode.trigger = { type: 'PATTERNS', values: nodeData.buttons };
        transformedNode.message.type = 'WHATSAPPFLOW';
        transformedNode.message.flowToken = nodeData?.flowToken;
        transformedNode.message.firstScreenName = nodeData?.firstScreenName;
        transformedNode.message.flowStatus = nodeData?.flowStatus;
        transformedNode.message.isCustomData = nodeData?.isCustomData || false;
        transformedNode.message.customData = nodeData?.customData;
        transformedNode.message.flowId = nodeData?.flowId;
        transformedNode.message.action = { output_context: outputContext || null };
      } else if (type === 'list') {
        transformedNode.message.type = 'LIST';
        // transformedNode.selectedEntityForm = nodeData?.selectedEntityForm;
        // transformedNode.parentEntityNode = nodeData?.parentEntityNode;
        // transformedNode.entityFormData = nodeData?.entityFormData;
        // transformedNode.optionPath = nodeData?.optionPath;
        transformedNode.sourceType = nodeData?.sourceType;
        if (nodeData?.sourceType === 'Entity') {
          transformedNode.entity_setting = modifyEntitySettingObj(nodeData?.entity_setting);
        } else if (nodeData?.sourceType === 'Leads') {
          transformedNode.lead_data_setting = nodeData?.lead_data_setting;
        } else if (nodeData.options) {
          transformedNode.trigger = { type: 'PATTERNS', values: nodeData.options };
          transformedNode.message.list = nodeData.options.map((optGroup: any, groupIndex) => ({
            title: optGroup?.title,
            options: optGroup?.options?.map((opt: any, index) => {
              const uniqueContext = fetchTargetBySourceHandle(
                `${id}-options-${groupIndex}-${index}`
              );
              const existingFlow = findExistingFlowNode(`${uniqueContext}`);
              if (existingFlow) {
                const flowData = existingFlow?.data?.selectedFlow;
                const flowFirstNode = flowData?.rules?.find((rule) => rule?.is_first);
                const baseOption = {
                  label: opt,
                  value: opt,
                  action: {
                    output_context: uniqueContext || null,
                    flow_id: flowId,
                  },
                };
                return baseOption;
              } else {
                const baseOption = {
                  label: opt,
                  value: opt,
                  action: {
                    output_context: uniqueContext || null,
                    flow_id: flowId,
                  },
                };
                return baseOption;
              }
            }),
          }));
        }
      } else if (type === 'product') {
        transformedNode.message.type = 'PRODUCT';
        transformedNode.message.action = { catalog_id: nodeData?.catalog?.id || '' };
        transformedNode.message.action.sections = nodeData?.sections?.map((optGroup: any) => ({
          ...optGroup,
          product_items: optGroup?.product_items?.map((opt: any) => {
            return {
              product_retailer_id: opt?.id,
              product_name: opt?.name,
            };
          }),
        }));
      } else if (type === 'catalog') {
        transformedNode.message.type = 'CATALOG';
        transformedNode.message.action = {
          thumbnail_product_retailer_id: nodeData?.catalog?.id || '',
        };
      } else if (type === 'intervention') {
        transformedNode.message.type = 'INTERVENTION';
        transformedNode.trigger = { type: 'PATTERNS', values: nodeData?.intervention || [] };
        const agents = nodeData?.agentList?.map(({ follow_me_number, name }: any) => ({
          follow_me_number,
          name,
        }));
        transformedNode.message.buttons = nodeData?.intervention
          ?.filter((item: any) => item?.enabled)
          ?.map((item: any) => {
            try {
              const uniqueContext = fetchTargetBySourceHandle(`${id}-${item?.value}`);
              const existingFlow = findExistingFlowNode(`${uniqueContext}`);
              const action: any = {
                label: item?.label || '',
                value: item?.type || '',
                type: item?.type || '',
              };
              if (existingFlow) {
                const flowData = existingFlow?.data?.selectedFlow;
                const flowFirstNode = flowData?.rules?.find((rule: any) => rule?.is_first);
                action.action = {
                  output_context: uniqueContext || null,
                  flow_id: flowId || null,
                };
              } else {
                action.action = {
                  output_context: uniqueContext || null,
                  flow_id: flowId || null,
                };
              }
              if (item?.type === 'click_to_call') {
                action.action.agent = agents || [];
              }
              return action;
            } catch (error) {
              return {
                label: item?.label || '',
                value: item?.type || '',
                type: item?.type || '',
                action: {
                  output_context: null,
                  flow_id: null,
                  agent: [],
                },
              };
            }
          });
      } else if (type === 'conditional') {
        transformedNode.message.type = 'CONDITIONAL';
        // const ifGroupEdge: any = flowObj?.edges?.find(
        //   (edge) => edge?.sourceHandle === nodeData?.ifGroup?.id
        // );
        transformedNode.ifGroup = {
          ...nodeData?.ifGroup,
          action: {
            output_context: fetchTargetBySourceHandle(nodeData?.ifGroup?.id) ?? null,
            flow_id: flowId,
          },
        };
        transformedNode.elseIfGroups = nodeData?.elseIfGroups?.map((rule, index) => ({
          ...rule,
          action: {
            output_context: fetchTargetBySourceHandle(rule?.id) ?? null,
            flow_id: flowId,
          },
        }));
        transformedNode.elseGroup = {
          action: {
            output_context: fetchTargetBySourceHandle(`${id}-else`) ?? null,
            flow_id: flowId,
          },
        };
      }

      if (['image', 'video', 'audio', 'document'].includes(type)) {
        transformedNode.message.media = { ...nodeData?.media };
      }
      if (nodeData?.triggerSettings?.isStatusEnable) {
        transformedNode.status = {};
        transformedNode.status.value = nodeData?.triggerSettings?.selectedStatus?.status_name ?? '';
        transformedNode.status.status_id =
          nodeData?.triggerSettings?.selectedStatus?.status_id ?? '';
      }
      // if (type === 'existingFlow') {
      //   console.log(nodeData, "nodeData");
      //   console.log(transformedNode, "transformedNode")
      // }
      return transformedNode;
    });

    return { rules };
  };
  const formatFieldMessages = (field, message) => {
    if (typeof message === 'object' && message !== null) {
      return `- ${field}:\n${Object.entries(message)
        .map(([key, subMsg]) => `  - ${key}: ${subMsg}`)
        .join('\n')}`;
    }
    return `- ${field}: ${message}`;
  };

  const handleSaveFlow = async (skipValidation: boolean) => {
    if (!flowObj) return;

    setIsSaving(true);
    try {
      if (!skipValidation) {
        const validationErrors = validateNodes(flowObj?.nodes);
        flowValidationObj(validationErrors);

        const nodes = flowObj?.nodes || [];
        const edges = flowObj?.edges || [];

        // if (nodes.length > 1) {
        //   // Get all connected node IDs
        //   const connectedNodeIds = new Set();
        //   edges.forEach((edge: Edge) => {
        //     connectedNodeIds.add(edge.source);
        //     connectedNodeIds.add(edge.target);
        //   });

        //   const unconnectedNodes = nodes.filter((node: Node) => !connectedNodeIds.has(node.id));

        //   if (unconnectedNodes.length > 0) {
        //     showSnackBar(
        //       `Error: The following nodes are not connected: ${unconnectedNodes
        //         .map((node) => node.id)
        //         .join(', ')}`,
        //       'error'
        //     );
        //     setIsSaving(false);
        //     return;
        //   }
        // }

        if (Object.keys(validationErrors).length > 0) {
          const errorMessage = Object.entries(validationErrors)
            ?.map(([nodeId, fields]) => {
              const fieldMessages = Object?.entries(fields)
                ?.map(([field, msg]) => formatFieldMessages(field, msg))
                ?.join('\n');
              return `Node ${nodeId}:\n${fieldMessages}`;
            })
            ?.join('\n\n');
          showSnackBar(`Validation Errors:\n\n${errorMessage}`, 'error');
          setIsSaving(false);
          return;
        }
      }

      await actions.createNodeEdges({ ...flowObj, flow_id: flowId });
      const result = convertNodesAndEdges(flowObj.nodes, flowObj.edges);
      let res: any = await actions.updateFlow(flowId, result);
      if (res.status === 200) {
        showSnackBar('Flow saved successfully!', 'success');
        const savedFlows = JSON.parse(localStorage.getItem('flowData') || '{}');
        delete savedFlows[flowId];
        localStorage.setItem('flowData', JSON.stringify(savedFlows));
      } else if (res?.status === 404) {
        showSnackBar(res?.data?.detail, 'error');
        navigate('/r/flow/list/');
      } else {
        showSnackBar('Failed to save flow. Please try again.', 'error');
      }
    } catch (error) {
      showSnackBar('Failed to save flow. Please try again.', 'error');
    } finally {
      setIsSaving(false);
      setIsModalOpen(false);
    }
  };

  const handleSaveButtonClick = () => {
    setIsModalOpen(true);
  };

  // const fetchLeadFormData = async (campaignId) => {
  //   try {
  //     let res = await leadFormActions.getFormsByCampaignId(campaignId);
  //     let formId = botFLowData?.lead_form_id?.toString() || '';
  //     if (res?.status === 200) {
  //       // let leadArrayData: any = Object.values(res?.data[formId]?.leads_form_items)?.map(
  //       //   (item: any) => {
  //       //     return { ...item };
  //       //   }
  //       // );
  //       const leadArrayData = staticColumnsMatch(
  //         Object.values(res?.data[formId]?.leads_form_items) ?? []
  //       );
  //       setLeadFormDataFields([...leadArrayData]);
  //     }
  //   } catch (error) {
  //     console.log(error, 'errorerror');
  //   }
  // };

  const fetchLeadFormData = async (campaignId: string) => {
    try {
      if (!campaignId) return null;

      if (webFormList?.[campaignId]) {
        return webFormList[campaignId];
      }

      const response = await leadFormActions.getFormsByCampaignId(campaignId);
      if (response?.status === 200) {
        const forms = response?.data ?? null;
        // merge into atom
        setWebFormList((prev: any) => ({
          ...(prev || {}),
          [campaignId]: forms,
        }));
        return forms;
      }
    } catch (error) {
      console.warn('getWebFormListByCampaign error', error);
    }
  };


  const fetchBotFlowDetails = async () => {
    let res = await actions.getBotFlowDetails(flowId);
    if (res?.status === 200) {
      setBotFlowData({ ...res?.data });
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    if (botFLowData?.lead_form_id && botFLowData?.campaign_id) {
      fetchLeadFormData(botFLowData?.campaign_id);
    }
  }, [botFLowData]);

  useEffect(() => {
    fetchBotFlowDetails();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} height="80vh" overflow="hidden">
        {/* <Box overflow="auto"> */}
        <Sidebar />
        {/* </Box> */}
        <Box flex={1}>
          <ChatCanvas />
        </Box>
      </Box>
      <Box>
        <ChatbotPopup />
        {/* <FlowCanvas onFlowChange={handleSave} /> */}
        <FlowCanvas onFlowChange={handleSaveButtonClick} />
      </Box>
      <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <DialogTitle>Save Flow</DialogTitle>
        <DialogContent>How would you like to save the flow?</DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleSaveFlow(true)} // Save as Draft
            disabled={isSaving}
          >
            Save as Draft
          </Button>
          <Button
            onClick={() => handleSaveFlow(false)} // Save with validation
            disabled={isSaving}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default ChatbotBuilder;
